'use client'
import React, { useState, useEffect } from 'react'
import styles from './Watchlist.module.css'
import { ConnectorManager } from '../ConnectorManager'
import { TickerItem } from '../types'
import { FavoritesPopup } from './FavoritesPopup'
import { useNintradeFavorites } from './hooks/useZerodhaFavorites'
import { useZerodhaWebSocket } from './hooks/useZerodhaWebSocket'
import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import {
  MagnifyingGlassIcon,
  ArrowsUpDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CurrencyDollarIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline'

/**
 * @uiComponent
 * @name Watchlist
 * @description The Watchlist component displays real-time market data for stocks on the Zerodha platform.
 * It allows users to view favorites, search through all available stocks, sort by various criteria,
 * and navigate to individual stock pages. Features WebSocket-based real-time price updates
 * and synchronized favorites management across the application.
 * @location Main left panel of the trading interface
 * @prop none - No external props, uses internal state management and hooks
 */

/**
 * @uiWorkflow
 * @name Add Stocks to Favorites on Zerodha
 * @description How to add stocks to your watchlist favorites for quick access
 * @platform zerodha
 * @step 1 Navigate to the Watchlist tab in the trading terminal [component: Watchlist] [action: navigate]
 * @step 2 Switch to the "All" tab to browse available stocks [component: Watchlist] [action: click]
 * @step 3 Use the search bar to find specific stocks by symbol [component: Watchlist] [action: input]
 * @step 4 Click the star icon next to any stock to add it to favorites [component: Watchlist] [action: click]
 * @step 5 Alternatively, click the "+ Add" button to open the favorites popup [component: Watchlist] [action: click]
 * @step 6 In the popup, search and select stocks to add [component: FavoritesPopup] [action: select]
 * @step 7 Switch back to the "Favorites" tab to view your selected stocks [component: Watchlist] [action: click]
 * @step 8 Enjoy real-time price updates via WebSocket connection [component: Watchlist] [action: observe]
 */

/**
 * @uiWorkflow
 * @name Monitor Real-time Stock Prices on Zerodha
 * @description How to track live price movements and market data with WebSocket updates
 * @platform zerodha
 * @step 1 Open the Watchlist component in the trading interface [component: Watchlist] [action: navigate]
 * @step 2 Add stocks to favorites for focused monitoring [component: Watchlist] [action: click] [notes: Click star icons or use + Add button]
 * @step 3 View the "Favorites" tab for your selected stocks [component: Watchlist] [action: click]
 * @step 4 Observe real-time price updates via WebSocket connection [component: Watchlist] [action: observe] [notes: Prices update automatically without page refresh]
 * @step 5 Sort stocks by price, change percentage, or volume for analysis [component: Watchlist] [action: click] [notes: Click on column headers or filter chips]
 * @step 6 Click on any stock symbol to view detailed charts and trading options [component: Watchlist] [action: click]
 * @step 7 Use the search function to quickly find specific stocks [component: Watchlist] [action: input]
 * @step 8 Monitor the WebSocket connection status for real-time updates [component: Watchlist] [action: observe]
 */

/**
 * @uiWorkflow
 * @name Navigate to Stock Details on Zerodha
 * @description How to access detailed stock information and trading interface
 * @platform zerodha
 * @step 1 Navigate to the Watchlist in the trading terminal [component: Watchlist] [action: navigate]
 * @step 2 Browse through "Favorites" or "All" tabs to find your stock [component: Watchlist] [action: click]
 * @step 3 Use the search bar to quickly locate a specific stock [component: Watchlist] [action: input]
 * @step 4 Click on the stock symbol to navigate to its detail page [component: Watchlist] [action: click]
 * @step 5 The page will open with exchange-specific URL format (.NS for NSE, .BS for BSE) [component: Watchlist] [action: observe]
 * @step 6 Access detailed charts, buy/sell options, and market depth [component: StockDetail] [action: navigate]
 * @step 7 Use the back button or breadcrumbs to return to the watchlist [component: Watchlist] [action: navigate]
 */

/**
 * @uiWorkflow
 * @name Sort and Filter Stocks on Zerodha Watchlist
 * @description How to organize and filter stocks for better market analysis
 * @platform zerodha
 * @step 1 Open the Watchlist component [component: Watchlist] [action: navigate]
 * @step 2 Use the search bar to filter stocks by symbol name [component: Watchlist] [action: input]
 * @step 3 Click on "Name" filter chip to sort alphabetically [component: Watchlist] [action: click]
 * @step 4 Click on "Price" filter chip to sort by current price [component: Watchlist] [action: click]
 * @step 5 Use "Change %" filter to sort by percentage movement [component: Watchlist] [action: click]
 * @step 6 Apply "Volume" filter to see most actively traded stocks [component: Watchlist] [action: click]
 * @step 7 Toggle between ascending and descending order by clicking again [component: Watchlist] [action: click]
 * @step 8 Combine search and sorting for precise stock discovery [component: Watchlist] [action: combine]
 */

/** Tabs at the top: Favorites, All */
const TABS = ['Favorites', 'All']

// In your Watchlist component
import { useRouter } from 'next/navigation'

export default function Watchlist() {
  // Add router
  const router = useRouter()
  const [activeTab, setActiveTab] = useState(TABS[0])
  const [tickers, setTickers] = useState<TickerItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [allSymbols, setAllSymbols] = useState<string[]>([])
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortConfig, setSortConfig] = useState<{
    key: keyof TickerItem | 'symbol'
    direction: 'asc' | 'desc'
  }>({
    key: 'volume',
    direction: 'desc'
  })
  const connectorManager = ConnectorManager.getInstance()

  // Use our custom hook for favorites management
  const { favorites, addFavorite, removeFavorite, toggleFavorite } =
    useNintradeFavorites()

  // Use WebSocket for real-time updates
  const { isConnected, error: wsError } = useZerodhaWebSocket({
    onTickerUpdate: (tickersData) => {
      // Extract all symbols for the search functionality
      setAllSymbols(tickersData.map(ticker => ticker.originalSymbol))

      // Filter based on active tab
      let filteredTickers = [...tickersData]
      if (activeTab === 'Favorites') {
        filteredTickers = filteredTickers.filter(ticker =>
          favorites.includes(ticker.originalSymbol)
        )
      }

      // Apply sorting
      sortTickers(filteredTickers)
    }
  })

  // Fetch only on mount / tab change
  useEffect(() => {
    fetchTickers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  // Re-fetch when favorites change, but only if we're on the Favorites tab
  useEffect(() => {
    if (activeTab === 'Favorites') {
      fetchTickers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favorites])

  // Add this function to handle symbol click
  const handleSymbolClick = (symbol: string, exchange: string) => {
    // Open in a new tab instead of navigating in the same tab
    // window.open(`/${symbol.toLowerCase()}`)
    // Navigate in the same tab
    const exchangeSuffix = exchange.toLowerCase() === 'nse' ? 'ns' : exchange.toLowerCase() === 'bse' ? 'bs' : 'ns'
    window.location.href = `/${symbol.toLowerCase()}.${exchangeSuffix}`
    // For future implementation with exchange type:
    // router.push(`/${symbol.toLowerCase()}.${exchangeSuffix}`)
  }

  const fetchTickers = async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Get the Zerodha connector
      const zerodhaConnector = connectorManager.getConnector('zerodha')

      if (!zerodhaConnector) {
        throw new Error('Zerodha connector not found or not connected')
      }

      // Fetch ticker data from the connector with proper typing
      const tickersData: TickerItem[] = await zerodhaConnector.getTickers()

      // Extract all symbols for the search functionality
      setAllSymbols(tickersData.map(ticker => ticker.originalSymbol))

      // Filter based on active tab
      let filteredTickers = [...tickersData]
      if (activeTab === 'Favorites') {
        filteredTickers = filteredTickers.filter(ticker =>
          favorites.includes(ticker.originalSymbol)
        )
      }

      // Apply sorting
      sortTickers(filteredTickers)
    } catch (err: any) {
      console.error('Error fetching tickers:', err)
      setError(
        err.message ||
          'Failed to fetch market data. Please ensure you are connected to Zerodha.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const sortTickers = (tickersToSort: TickerItem[]) => {
    const sortedTickers = [...tickersToSort].sort((a, b) => {
      if (sortConfig.key === 'symbol') {
        return sortConfig.direction === 'asc'
          ? a.originalSymbol.localeCompare(b.originalSymbol)
          : b.originalSymbol.localeCompare(a.originalSymbol)
      }

      const aValue = a[sortConfig.key]
      const bValue = b[sortConfig.key]

      if (sortConfig.direction === 'asc') {
        return aValue < bValue ? -1 : 1
      } else {
        return aValue > bValue ? -1 : 1
      }
    })

    setTickers(sortedTickers)
  }

  const handleSort = (key: keyof TickerItem | 'symbol') => {
    let direction: 'asc' | 'desc' = 'desc'

    if (sortConfig.key === key) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc'
    }

    setSortConfig({ key, direction })
    sortTickers(tickers)
  }

  const handleAddFavorite = (symbol: string) => {
    addFavorite(symbol)
    setIsPopupOpen(false)
  }

  const handleToggleFavorite = (symbol: string) => {
    toggleFavorite(symbol)
  }

  const filteredTickers = searchTerm
    ? tickers.filter(ticker =>
        ticker.originalSymbol.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : tickers

  function renderTabContent() {
    if (isLoading)
      return (
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <p>Loading market data...</p>
        </div>
      )

    if (error)
      return (
        <div className={styles.errorState}>
          <p>{error}</p>
          <button onClick={fetchTickers} className={styles.retryButton}>
            Retry
          </button>
        </div>
      )

    if (tickers.length === 0)
      return (
        <div className={styles.emptyState}>
          {activeTab === 'Favorites' ? (
            <>
              <p>No favorites added yet</p>
              <button
                onClick={() => setIsPopupOpen(true)}
                className={styles.addButton}
              >
                Add Favorites
              </button>
            </>
          ) : (
            <p>No data available</p>
          )}
        </div>
      )

    return (
      <>
        <div className={styles.tableControls}>
          <div className={styles.searchContainer}>
            <MagnifyingGlassIcon
              className={styles.searchIcon}
              width={18}
              height={18}
            />
            <input
              type="text"
              placeholder="Search symbols..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          <div className={styles.filterChips}>
            <div
              className={`${styles.filterChip} ${sortConfig.key === 'symbol' ? styles.active : ''}`}
              onClick={() => handleSort('symbol')}
            >
              Name
              {sortConfig.key === 'symbol' &&
                (sortConfig.direction === 'asc' ? (
                  <ArrowUpIcon className={styles.chipIcon} />
                ) : (
                  <ArrowDownIcon className={styles.chipIcon} />
                ))}
            </div>

            <div
              className={`${styles.filterChip} ${sortConfig.key === 'price' ? styles.active : ''}`}
              onClick={() => handleSort('price')}
            >
              Price
              <CurrencyDollarIcon className={styles.chipIcon} />
              {sortConfig.key === 'price' &&
                (sortConfig.direction === 'asc' ? (
                  <ArrowUpIcon className={styles.chipIcon} />
                ) : (
                  <ArrowDownIcon className={styles.chipIcon} />
                ))}
            </div>

            <div
              className={`${styles.filterChip} ${sortConfig.key === 'priceChangePercent' ? styles.active : ''}`}
              onClick={() => handleSort('priceChangePercent')}
            >
              Change %
              {sortConfig.key === 'priceChangePercent' &&
                (sortConfig.direction === 'asc' ? (
                  <ArrowUpIcon className={styles.chipIcon} />
                ) : (
                  <ArrowDownIcon className={styles.chipIcon} />
                ))}   
            </div>

            <div
              className={`${styles.filterChip} ${sortConfig.key === 'volume' ? styles.active : ''}`}
              onClick={() => handleSort('volume')}
            >
              Volume
              <ChartBarIcon className={styles.chipIcon} />
              {sortConfig.key === 'volume' &&
                (sortConfig.direction === 'asc' ? (
                  <ArrowUpIcon className={styles.chipIcon} />
                ) : (
                  <ArrowDownIcon className={styles.chipIcon} />
                ))}
            </div>
          </div>

          {filteredTickers.length > 0 && (
            <div className={styles.resultsCount}>
              {filteredTickers.length} of {tickers.length}
            </div>
          )}
        </div>

        <div className={styles.tickersTable}>
          <table>
            <tbody>
              {filteredTickers.map(ticker => (
                <tr key={ticker.originalSymbol} className={styles.tickerRow}>
                  <td
                    className={`${styles.symbolCell} ${styles.clickable}`}
                    onClick={() => handleSymbolClick(ticker.originalSymbol, ticker.exchange)}
                  >
                    {ticker.originalSymbol}
                  </td>
                  <td className={styles.priceCell}>
                  ₹{ticker.price.toFixed(ticker.price < 0.01 ? 8 : 2)}
                  </td>
                  <td
                    className={`${styles.changeCell} ${ticker.priceChangePercent >= 0 ? styles.profit : styles.loss}`}
                  >
                    {ticker.priceChangePercent >= 0 ? '+' : ''}
                    {ticker.priceChangePercent.toFixed(2)}%
                  </td>
                  <td className={styles.volumeCell}>
                  ₹{ticker.volume.toLocaleString()}
                  </td>
                  <td className={styles.favoriteCell}>
                    <button
                      className={styles.favoriteButton}
                      onClick={() => handleToggleFavorite(ticker.originalSymbol)}
                      title={
                        favorites.includes(ticker.originalSymbol)
                          ? 'Remove from favorites'
                          : 'Add to favorites'
                      }
                    >
                      {favorites.includes(ticker.originalSymbol) ? (
                        <StarIconSolid
                          className={styles.starIconFilled}
                          width={18}
                          height={18}
                        />
                      ) : (
                        <StarIconOutline
                          className={styles.starIcon}
                          width={18}
                          height={18}
                        />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    )
  }

  return (
    <div className={`${styles.watchlistContainer} ${styles.themeVariables}`}>
      <div className={styles.tabHeader}>
        {TABS.map(tab => (
          <div
            key={tab}
            className={`${styles.tab} ${tab === activeTab ? styles.active : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </div>
        ))}
        <button
          className={styles.addFavoriteButton}
          onClick={() => setIsPopupOpen(true)}
        >
          + Add
        </button>
      </div>
      <div className={styles.tabContent}>{renderTabContent()}</div>

      <FavoritesPopup
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        onAddFavorite={handleAddFavorite}
        currentFavorites={favorites}
        availableSymbols={allSymbols}
      />
    </div>
  )
}
